/**
 * `/api/v1/stocks/…` responses, mapped to the shapes the four screens take as props.
 *
 * 🔴 **Why a layer at all, rather than changing the screens.** The generated client's types are
 * derived from the OpenAPI schema, where almost every array and most scalars are optional — a
 * `ScreenerRunOut.buy?: Array<…>` and a `ranAt?: string | null`. The screens were written against
 * types where those are required, because a screen that has to ask "did the server send `buy`?" at
 * every use site is a screen where the answer eventually gets assumed. This file answers it once.
 *
 * ⚠ **Absence is mapped to a stated value, never to a plausible one.** A missing `ranAt` becomes an
 * empty string, which the timestamp renderer already shows as `—`; it does not become "now". A
 * missing count becomes `0` only where the count is *of a list this response carries* — never where
 * it is a figure the server would have had to compute, because a zero the operator reads as
 * "measured zero" when it means "not sent" is precisely the class of quiet wrong answer this
 * project keeps finding.
 *
 * ⚠ `spend` and `preRegistration` are the two blocks the API does not carry yet. They are surfaced
 * as explicit nulls rather than zeros, and `adaptCoverage`/`adaptScorecard` say so — see the notes
 * on each.
 */

import type {
  CompanyOut,
  CoverageOut,
  ScorecardOut,
  ScreenerRowOut,
  ScreenerRunOut,
} from '../../api/generated';
import type {
  Company,
  Coverage,
  CoverageTier,
  Scorecard,
  ScreenerRow,
  ScreenerRun,
} from './model';

/** A tier string from the wire, narrowed. Anything unrecognised reads as `LISTED`, the weakest. */
function tier(value: string | null | undefined): CoverageTier {
  return value === 'FULL' || value === 'PRICE' ? value : 'LISTED';
}

function row(out: ScreenerRowOut): ScreenerRow {
  return {
    marketCap: out.marketCap ?? undefined,
    revenueYoy: out.revenueYoy ?? undefined,
    revenueAcceleration: out.revenueAcceleration ?? undefined,
    relativeStrength: out.relativeStrength ?? undefined,
    profitable: out.profitable ?? undefined,
    isin: out.isin,
    ticker: out.ticker,
    name: out.name,
    price: out.price ?? '',
    tier: tier(out.tier),
    rules: (out.rules ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      reading: r.reading ?? '',
      passed: Boolean(r.passed),
    })),
    reason: out.reason ?? '',
    // 🔴 Empty is meaningful here and must survive the mapping. `API-and-UI.md` is explicit that a
    // BUY read without its counter-case is the failure the refutation pass exists to prevent, and
    // the screens render "(none recorded — dossier missing or rejected)" for an empty string.
    // Substituting a cheerful default would erase the distinction.
    against: out.against ?? '',
    ...(out.caution ? { caution: out.caution } : {}),
    ...(out.held?.changePct ? { held: { changePct: out.held.changePct } } : {}),
    ...(out.failedRule ? { failedRule: out.failedRule } : {}),
    // ⚠ `rank` is omitted rather than defaulted to 0: a rank of zero would read as "first", and an
    // unranked row is one the run did not order, not one that came top.
    ...(out.rank ? { rank: out.rank } : {}),
    ...(out.why ? { why: out.why } : {}),
    // ⚠ `!= null` rather than truthy: a score of 0 is a measurement and must survive the
    // mapping, where `undefined` means the run did not rate the row at all.
    ...(out.score != null ? { score: out.score } : {}),
    // ⚠ Same reason, and it bites harder here: 0 is the *middle* of this scale, meaning buying and
    // selling balanced. A truthy check would drop exactly the readings that say "neither side won"
    // and leave the row looking as though nothing was measured.
    ...(out.accumulation != null ? { accumulation: out.accumulation } : {}),
    ...(out.accumulationAsOf ? { accumulationAsOf: out.accumulationAsOf } : {}),
    // ⚠ `!= null` again: risk 0 is a real measurement (a quiet, liquid, fully-covered name) and
    // must survive, while `undefined` means the market could not be measured at all.
    ...(out.risk != null ? { risk: out.risk } : {}),
    ...(out.riskNotes?.length ? { riskNotes: out.riskNotes } : {}),
    // ⚠ Omitted when empty rather than carried as `[]`, so the card can distinguish *this run had
    // nothing to say about this name* from *the field is not served yet*. An older frozen run has
    // no `highlights` key at all, and it renders as the previous card rather than as a blank slot.
    ...(out.highlights?.length ? { highlights: out.highlights } : {}),
    ...(out.growth?.length ? { growth: out.growth } : {}),
    // ⚠ Kept even when a family is missing from the object: an absent family had no inputs, and
    // `FamilyBars` draws that as unmeasured. Substituting 0 would claim a measurement of zero.
    ...(out.families && Object.keys(out.families).length ? { families: out.families } : {}),
    ...(out.covered ? { covered: out.covered } : {}),
    ...(out.summary ? { summary: out.summary } : {}),
  };
}

export function adaptScreenerRun(out: ScreenerRunOut): ScreenerRun {
  return {
    runId: out.runId,
    // ⚠ Empty rather than a substituted clock. `ui/timestamps` renders it as `—`.
    ranAt: out.ranAt ?? '',
    rulesetVersion: out.rulesetVersion,
    screened: out.screened,
    buy: (out.buy ?? []).map(row),
    sell: (out.sell ?? []).map(row),
    review: (out.review ?? []).map(row),
    hold: (out.hold ?? []).map((h) => ({
      ticker: h.ticker,
      name: h.name,
      note: h.note ?? '',
    })),
  };
}

/**
 * ⚠ **`window` and `preRegistration` are not on the wire.**
 *
 * The scorecard endpoint returns the measured excess, the benchmark and the sample size. The
 * *window* is a property of the pre-registration document rather than of a query result, and the
 * pre-registration itself lives in the vault. Both are rendered as stated gaps: an empty `window`
 * and a `claim` that says where the real one is. Inventing "12 weeks" here would put a number the
 * server never computed next to numbers it did.
 */
export function adaptScorecard(out: ScorecardOut): Scorecard {
  return {
    excessPct: out.excessPct ?? '',
    benchmark: out.benchmark ?? 'ACWI',
    n: out.n ?? 0,
    window: out.window,
    picks: (out.picks ?? []).map((p) => ({
      ticker: p.ticker,
      publishedOn: p.publishedOn,
      priceAtPick: p.priceAtPick ?? '',
      family: p.family ?? '',
      marks: (p.marks ?? []).map((m) => ({
        weeks: (m.weeks === 1 || m.weeks === 4 ? m.weeks : 12) as 1 | 4 | 12,
        excessPct: m.excessPct ?? null,
      })),
    })),
    families: (out.families ?? []).map((f) => ({
      name: f.name,
      note: f.note ?? '',
      n: f.n ?? 0,
      excessPct: f.excessPct ?? '',
    })),
    // 🔴 Left undefined, never zeroed. `0 positions · — excess · n=0` reads as a measurement of
    // nothing when the truth is that the endpoint does not carry these blocks: the operator's own
    // broker performance and the pre-registration document. The screen says which.
    conclusive: out.conclusive,
  };
}

/**
 * ⚠ **`spend` is not on the wire, and it is reported as absent rather than as zero.**
 *
 * The spend block measures LLM dossier cost against a monthly cap, and phases 8–9 are deferred —
 * there are no dossiers, so there is no spend. A row of zeros would read as "measured, and it is
 * nothing", which is a different claim from "this is not built". `cap: 0` is the tell the screen
 * uses to say so.
 */
export function adaptCoverage(out: CoverageOut): Coverage {
  const counts = (out.counts ?? []).map((c) => ({ tier: c.tier, n: c.n ?? 0 }));
  return {
    fullPct: out.fullPct ?? 0,
    atFull: out.atFull ?? 0,
    screenable: out.screenable ?? 0,
    counts,
    sources: (out.sources ?? []).map((s) => ({
      name: s.name,
      state: s.state === 'OK' || s.state === 'DEGRADED' ? s.state : 'SCHEDULED',
      lastSuccess: s.lastSuccess ?? '',
      staleness: s.staleness ?? '',
      errorRate: s.errorRate ?? '',
    })),
    resolution: (out.resolution ?? []).map((r) => ({
      label: r.label ?? '',
      value: r.value ?? '',
    })),
    ...(out.degradedNote ? { degradedNote: out.degradedNote } : {}),
    exclusions: (out.exclusions ?? []).map((e) => ({ reason: e.reason, n: e.n ?? 0 })),
    // 🔴 **Read, not fabricated.** This block used to be six hardcoded zeros, which is why the
    // Evaluation cadence panel showed `$0.00` for everything and its two controls looked dead:
    // every figure on that panel is derived from `perDossier`, and zero times anything is zero.
    // ⚠ The optional fields stay `undefined` rather than defaulting to 0 — the panel renders
    // "not recorded" for absent, and 0 would claim the wallet is empty.
    spend: {
      runs: out.spend?.runs ?? 0,
      written: out.spend?.written ?? 0,
      rejected: out.spend?.rejected ?? 0,
      tokensIn: out.spend?.tokensIn ?? 0,
      tokensOut: out.spend?.tokensOut ?? 0,
      tokensCached: out.spend?.tokensCached ?? 0,
      ...(out.spend?.monthStart ? { monthStart: out.spend.monthStart } : {}),
      ...(out.spend?.cachedPct != null ? { cachedPct: out.spend.cachedPct } : {}),
      ...(out.spend?.balance != null ? { balance: out.spend.balance } : {}),
      ...(out.spend?.balanceCurrency ? { balanceCurrency: out.spend.balanceCurrency } : {}),
      ...(out.spend?.balanceAsOf ? { balanceAsOf: out.spend.balanceAsOf } : {}),
      ...(out.spend?.perMillionTokens != null
        ? { perMillionTokens: out.spend.perMillionTokens }
        : {}),
      ...(out.spend?.perRun != null ? { perRun: out.spend.perRun } : {}),
    },
  };
}


/**
 * ⚠ **Three blocks are still not on the wire and are left undefined:** `changes` (a per-company
 * history means diffing consecutive frozen runs), `trend` (a `stock_metrics` read) and `flow` (a
 * per-company summary of `stock_events`). The screen states each gap rather than drawing an empty
 * timeline, which would read as "nothing has changed".
 *
 * 🔴 `verdict` is `null` for a company the newest run never scored — a name added to the universe
 * today, or one excluded before scoring. It is **not** defaulted to `HOLD`, which would be a verdict
 * nobody reached, and `noVerdictReason` carries the server's own sentence.
 */
export function adaptCompany(out: CompanyOut): Company {
  const verdict =
    out.verdict === 'BUY' || out.verdict === 'SELL' || out.verdict === 'REVIEW' || out.verdict === 'HOLD'
      ? out.verdict
      : null;
  return {
    isin: out.isin,
    ticker: out.ticker,
    name: out.name,
    price: out.price ?? '',
    priceAsOf: out.priceAsOf ?? '',
    verdict,
    ...(out.reason ? { noVerdictReason: out.reason } : {}),
    rulesetVersion: out.rulesetVersion ?? '',
    scoredAt: out.scoredAt ?? '',
    rules: (out.rules ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      reading: r.reading ?? '',
      passed: Boolean(r.passed),
    })),
    against: out.against ?? '',
    ...(out.score != null ? { score: out.score } : {}),
    ...(out.risk != null ? { risk: out.risk } : {}),
    ...(out.riskNotes?.length ? { riskNotes: out.riskNotes } : {}),
    // ⚠ Omitted when empty, so the page can tell "this run said nothing about it" from "the field
    // is not served" — the same reason the screener row omits rather than defaults them.
    ...(out.highlights?.length ? { highlights: out.highlights } : {}),
    ...(out.growth?.length ? { growth: out.growth } : {}),
    ...(out.families && Object.keys(out.families).length ? { families: out.families } : {}),
    ...(out.covered ? { covered: out.covered } : {}),
    ...(out.summary ? { summary: out.summary } : {}),
    identity: {
      cik: out.identity.cik ?? null,
      method: out.identity.method,
      confidence: out.identity.confidence ?? '',
      listings: (out.identity.listings ?? []).map((l) => ({
        line: String(l['line'] ?? l['ticker'] ?? ''),
        primary: Boolean(l['primary']),
      })),
      tier: tier(out.identity.tier),
    },
    fundamentals: (out.fundamentals ?? []).map((f) => ({
      metric: f.metric,
      value: f.value ?? null,
      filed: f.filed,
      ...(f.periodEnd ? { periodEnd: f.periodEnd } : {}),
      ...(f.concept ? { concept: f.concept } : {}),
      ...(f.basis ? { basis: f.basis } : {}),
      // ⚠ Carried as a boolean rather than omitted when false: the row renders a marker on true and
      // nothing on false, and an absent key would be indistinguishable from a fresh figure.
      stale: Boolean(f.stale),
      ...(f.lagDays ? { lagDays: f.lagDays } : {}),
    })),
    events: (out.events ?? []).map((e) => ({
      type: e.type,
      what: e.what,
      citation: e.citation,
      lagDays: e.lagDays,
    })),
    ...(out.price52w
      ? {
          price52w: {
            low: out.price52w.low,
            high: out.price52w.high,
            pctOfRange: out.price52w.pctOfRange ?? null,
            // The note says what the range was measured over, so the bar is not a bare mark.
            note: `52-week range over ${out.price52w.bars.toLocaleString()} daily closes.`,
          },
        }
      : {}),
    dossier: {
      asOf: out.dossier?.asOf ?? '',
      modelId: out.dossier?.modelId ?? '',
      promptVersion: out.dossier?.promptVersion ?? '',
      bull: (out.dossier?.bull ?? []).map((b) => ({
        text: String(b['text'] ?? ''),
        citation: String(b['citation'] ?? ''),
      })),
      bear: (out.dossier?.bear ?? []).map((b) => ({
        text: String(b['text'] ?? ''),
        citation: String(b['citation'] ?? ''),
      })),
      refutation: out.dossier?.refutation ?? '',
      falsifiers: out.dossier?.falsifiers ?? [],
      unknowns: out.dossier?.unknowns ?? [],
      ...(out.dossier?.rejectedReason ? { rejectedReason: out.dossier.rejectedReason } : {}),
    },
  };
}
